import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import { getPages, messageFor, pagesKey, type FunnelTotals, type PageSummary } from "./api";
import { ALL_FUNNELS, FunnelControls, selectedFunnel, useControls, WindowControl, type Controls } from "./controls";
import { PAGE, PageDetail, SECTION } from "./PageDetail";
import { PendingBatches, RecoverVariantButton, RetireVariantButton } from "./BulkActions";
import { NewLanguageButton } from "./WriteActions";
import { OrdersStamp, OrdersUnavailable } from "./OrderFigures";
import {
  getFunnelPageFigures,
  getFunnelSources,
  getFunnelStats,
  statsListKey,
  statsPageFiguresKey,
  statsSourcesKey,
  type FunnelStatsList,
  type PageFigures,
} from "./stats-api";
import {
  figuresForPage,
  formatMoney,
  formatOrders,
  formatRate,
  PAGE_ORDERS_CAPTION,
  sourceFilterSpec,
  sourceLabel,
  sourceParam,
  unattributedLine,
  unmatchedPagesLine,
} from "./stats-format";
import {
  formatFigure,
  formatLaunch,
  formatPct,
  formatSignups,
  funnelName,
  funnelRows,
  funnelSummaryLine,
  languageChips,
  versionChips,
  languageName,
  pageStatusLabel,
  pageViewUrl,
  type FunnelRow,
} from "./format";

// UX spec S6 (funnel list) and S7 (the page folders in one funnel), read-only.
// Spec: transparency specs/2026-09-24-landing-r4-funnel-pages.md; a page row opens S8 (R5). Every figure is the
// Worker's; this tab only groups and labels (I-4). R6 adds "+ New version" (UX F5).

const numeric = { meta: { align: "right" as const } };

/** A funnel row with the funnel stats API's orders beside it; `undefined` while loading or when it has no row. */
type FunnelWithOrders = FunnelRow & { orders?: number; revenue_gbp?: number };

function withOrders(rows: FunnelRow[], stats: FunnelStatsList | undefined): FunnelWithOrders[] {
  return rows.map((row) => {
    const match = stats?.funnels.find((funnel) => funnel.slug === row.funnel_slug);
    return { ...row, orders: match?.orders, revenue_gbp: match?.revenue_gbp };
  });
}

const FUNNEL_COLUMNS: ColumnDef<FunnelWithOrders, unknown>[] = [
  { id: "funnel", header: "Funnel", accessorFn: (row) => funnelName(row.funnel_slug) },
  { id: "live", header: "Live pages", accessorFn: (row) => String(row.live_pages), ...numeric },
  { id: "pages", header: "Pages", accessorFn: (row) => String(row.pages), ...numeric },
  { id: "views", header: "Views", accessorFn: (row) => formatFigure(row.views), ...numeric },
  { id: "visitors", header: "Visitors", accessorFn: (row) => formatFigure(row.visitors), ...numeric },
  { id: "taps", header: "Taps", accessorFn: (row) => formatFigure(row.clicks), ...numeric },
  { id: "signups", header: "Sign-ups", accessorFn: (row) => formatSignups(row.signups), ...numeric },
  { id: "conversion", header: "Conversion", accessorFn: (row) => formatPct(row.conversion_pct), ...numeric },
  { id: "orders", header: "Orders", accessorFn: (row) => formatOrders(row.orders), ...numeric },
  { id: "revenue", header: "Revenue", accessorFn: (row) => formatMoney(row.revenue_gbp), ...numeric },
];

/** Opens the live page in a new window without opening the page detail behind it. */
function PageName({ page }: { page: PageSummary }) {
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();
  return (
    <span className="inline-flex items-center gap-2">
      <span>{page.folder}</span>
      {page.status !== "retired" && (
        <a
          href={pageViewUrl(page)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          onKeyDown={stop}
          aria-label={`Open ${page.folder} in a new window`}
          title={`Open ${pageViewUrl(page)}`}
          className="text-ks-purple underline-offset-2 hover:underline"
        >
          ↗
        </a>
      )}
    </span>
  );
}

/** A page row with the funnel stats API's figures for it; `stats` is `undefined` while loading or with no activity. */
export type PageWithFigures = PageSummary & { stats?: PageFigures };

/**
 * With every source, traffic and sign-ups are the Worker's live figures. With one
 * source chosen they are the funnel stats API's (daily), the only place traffic is
 * split by source; taps are not split by source, so they show a dash. Orders,
 * revenue and revenue per visitor are always the funnel stats API's.
 */
function buildPageColumns(bySource: boolean, loading: boolean): ColumnDef<PageWithFigures, unknown>[] {
  // While the stats query runs its cells read "…", so loading never looks like "no activity" (–).
  const stat = (format: () => string) => (loading ? "…" : format());
  return [
    { id: "page", header: "Page", accessorFn: (row) => row.folder, cell: ({ row }) => <PageName page={row.original} /> },
    { id: "language", header: "Language", accessorFn: (row) => languageName(row.locale) },
    { id: "version", header: "Version", accessorFn: (row) => row.variant.toUpperCase() },
    { id: "status", header: "Status", accessorFn: (row) => pageStatusLabel(row) },
    { id: "launch", header: "Launches", accessorFn: (row) => formatLaunch(row.launch_at) },
    {
      id: "views",
      header: "Views",
      accessorFn: (row) => (bySource ? stat(() => formatFigure(row.stats?.views)) : formatFigure(row.views)),
      ...numeric,
    },
    {
      id: "visitors",
      header: "Visitors",
      accessorFn: (row) => (bySource ? stat(() => formatFigure(row.stats?.visitors)) : formatFigure(row.visitors)),
      ...numeric,
    },
    { id: "taps", header: "Taps", accessorFn: (row) => (bySource ? "–" : formatFigure(row.clicks)), ...numeric },
    {
      id: "signups",
      header: "Sign-ups",
      accessorFn: (row) => (bySource ? stat(() => formatFigure(row.stats?.leads)) : formatSignups(row.signups)),
      ...numeric,
    },
    {
      id: "conversion",
      header: "Conversion",
      accessorFn: (row) =>
        bySource ? stat(() => formatRate(row.stats?.lead_rate)) : formatPct(row.conversion_pct ?? null),
      ...numeric,
    },
    { id: "orders", header: "Orders", accessorFn: (row) => stat(() => formatOrders(row.stats?.orders)), ...numeric },
    { id: "revenue", header: "Revenue", accessorFn: (row) => stat(() => formatMoney(row.stats?.revenue_gbp)), ...numeric },
    {
      id: "revenue_per_visitor",
      header: "Revenue per visitor",
      accessorFn: (row) => stat(() => formatMoney(row.stats?.revenue_per_visitor)),
      ...numeric,
    },
  ];
}

// Built once per (source chosen, loading) state, so the table keeps stable column identity.
const PAGE_COLUMNS = {
  all: buildPageColumns(false, false),
  allLoading: buildPageColumns(false, true),
  bySource: buildPageColumns(true, false),
  bySourceLoading: buildPageColumns(true, true),
};

export function pageColumns(bySource: boolean, loading = false): ColumnDef<PageWithFigures, unknown>[] {
  if (bySource) return loading ? PAGE_COLUMNS.bySourceLoading : PAGE_COLUMNS.bySource;
  return loading ? PAGE_COLUMNS.allLoading : PAGE_COLUMNS.all;
}

/** The URL keys this tab owns, so the Split test tab's funnel choice never opens a funnel here. */
const OPEN = "open";
const LANG = "lang";
const VERSION = "version";
const SOURCE = "src";

export function FunnelsTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const pages = useQuery({
    queryKey: pagesKey(controls.days),
    queryFn: () => getPages(controls.days),
  });
  // Orders per funnel: an independent read, so the list still opens without it.
  const stats = useQuery({
    queryKey: statsListKey(controls.days),
    queryFn: () => getFunnelStats(controls.days),
  });

  if (pages.isError) {
    return (
      <Alert>
        <AlertTitle>Funnels unavailable</AlertTitle>
        <AlertDescription>{messageFor(pages.error, areaName)}</AlertDescription>
      </Alert>
    );
  }

  const data = pages.data?.data;
  const funnels = data?.funnels ?? [];
  const open = typeof controls.values[OPEN] === "string" ? (controls.values[OPEN] as string) : null;
  const selected = open ? funnels.find((f) => f.funnel_slug === open) : undefined;

  const openPage = typeof controls.values[PAGE] === "string" ? (controls.values[PAGE] as string) : null;
  if (openPage) {
    // Back always lands on the page's own funnel, even from a link that carried only the page.
    return (
      <PageDetail
        controls={controls}
        folder={openPage}
        areaName={areaName}
        onBack={(funnelSlug) =>
          controls.onChange({ ...controls.values, [OPEN]: funnelSlug ?? open, [PAGE]: null, [SECTION]: null })
        }
      />
    );
  }

  if (selected) {
    return (
      <FunnelPages
        controls={controls}
        funnelSlug={selected.funnel_slug}
        pages={selected.pages}
        totals={selected.totals}
        areaName={areaName}
      />
    );
  }

  // The drop-down narrows the list (FN-1); All funnels is the default and shows the full list.
  const filter = selectedFunnel(controls.funnel, funnels, true) as string;
  const shownFunnels = filter === ALL_FUNNELS ? funnels : funnels.filter((f) => f.funnel_slug === filter);
  const rows = withOrders(funnelRows(shownFunnels), stats.data?.data);
  // Orders from no funnel belong to the All view only.
  const unattributed = filter === ALL_FUNNELS ? unattributedLine(stats.data?.data.unattributed) : null;
  return (
    <div className="flex flex-col gap-4">
      <FunnelControls controls={controls} funnels={funnels} selected={filter} allowAll />
      {stats.isError && <OrdersUnavailable error={stats.error} areaName={areaName} />}
      {data && !data.counts_available && (
        <p className="text-sm text-muted-foreground">Sign-up counts are unavailable. Pages still work.</p>
      )}
      <DataTable
        columns={FUNNEL_COLUMNS}
        rows={rows}
        pending={pages.isPending}
        page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
        onPageChange={() => {}}
        onRowClick={(row) =>
          controls.onChange({
            ...controls.values,
            [OPEN]: row.funnel_slug,
            [LANG]: null,
            [VERSION]: null,
            [SOURCE]: null,
            [PAGE]: null,
          })
        }
        emptyMessage="No funnels yet. A funnel groups the landing pages for one campaign."
        caption={`Landing page funnels, sign-ups and orders over the last ${controls.days} days`}
      />
      {!stats.isError && (
        <OrdersStamp dataAsOf={stats.data?.meta.data_as_of} caption="Orders are matched to a funnel once a day." />
      )}
      {unattributed && <p className="text-sm text-muted-foreground">{unattributed}</p>}
    </div>
  );
}

function FunnelPages({
  controls,
  funnelSlug,
  pages,
  totals,
  areaName,
}: {
  controls: Controls;
  funnelSlug: string;
  pages: PageSummary[];
  totals?: FunnelTotals;
  areaName: string;
}) {
  const source = sourceParam(controls.values[SOURCE]);
  const bySource = source !== null;
  const figures = useQuery({
    queryKey: statsPageFiguresKey(funnelSlug, controls.days, source),
    queryFn: () => getFunnelPageFigures(funnelSlug, controls.days, source),
  });
  // The filter's choices: every source with traffic, sign-ups or orders in the window.
  const sources = useQuery({
    queryKey: statsSourcesKey(funnelSlug, controls.days),
    queryFn: () => getFunnelSources(funnelSlug, controls.days),
  });
  const figureRows = figures.data?.data.rows;
  const chips = languageChips(pages);
  const lang = typeof controls.values[LANG] === "string" ? (controls.values[LANG] as string) : null;
  const version = typeof controls.values[VERSION] === "string" ? (controls.values[VERSION] as string) : null;
  const versions = versionChips(pages);
  const shown: PageWithFigures[] = pages
    .filter((p) => (!lang || p.locale === lang) && (!version || p.variant === version))
    .map((p) => ({ ...p, stats: figuresForPage(figureRows, p) }));
  const current = shown.filter((p) => p.status !== "retired");
  const retired = shown.filter((p) => p.status === "retired");

  const langSpec: FilterSpec = {
    name: LANG,
    label: "Language",
    kind: "choice",
    // "All" is the empty value: the shell drops an empty filter from the URL.
    options: [
      { value: "", label: "All" },
      ...chips.map((chip) => ({
        value: chip.locale,
        label: `${languageName(chip.locale)} ${formatSignups(chip.signups)}`,
      })),
    ],
  };

  const versionSpec: FilterSpec = {
    name: VERSION,
    label: "Split-test version · live pages",
    kind: "choice",
    options: [
      { value: "", label: "All" },
      ...versions.map((chip) => ({
        value: chip.variant,
        label: `${chip.variant.toUpperCase()} ${chip.pages}`,
      })),
    ],
  };

  // A source picked from the URL stays a chip even when it has no traffic in this window,
  // so the filter can always be seen and cleared.
  const sourceSpec = sourceFilterSpec(SOURCE, sources.data?.data.rows ?? [], source);
  const unmatched = unmatchedPagesLine(figures.data?.data.unmatched);

  const groups = [
    { id: "current", title: "Pages", rows: current, tone: "primary" as const },
    ...(retired.length > 0
      ? [{ id: "retired", title: `Retired (${retired.length})`, rows: retired, tone: "inverted" as const }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          onClick={() =>
            controls.onChange({ ...controls.values, [OPEN]: null, [LANG]: null, [VERSION]: null, [SOURCE]: null })
          }
        >
          ← Funnels
        </Button>
        <h2 className="text-lg font-semibold text-ks-ink">{funnelName(funnelSlug)}</h2>
        <div className="ml-auto flex flex-wrap gap-2">
          <NewLanguageButton
            funnelSlug={funnelSlug}
            pages={pages}
            onCreated={(folder) => controls.onChange({ ...controls.values, [PAGE]: folder })}
          />
          <RetireVariantButton funnelSlug={funnelSlug} pages={pages} />
          <RecoverVariantButton funnelSlug={funnelSlug} pages={pages} />
        </div>
      </div>
      <PendingBatches funnelSlug={funnelSlug} />
      <p className="text-base text-ks-ink" aria-live="polite">
        {funnelSummaryLine(pages, controls.days, totals)}
      </p>
      <p className="text-sm text-muted-foreground">
        New split-test versions are written with /landing-build from an angle brief. Each one appears here as a
        pending change to preview and approve.
      </p>
      <WindowControl controls={controls} />
      <div className="flex flex-col gap-2 overflow-x-auto">
        {((sources.data?.data.rows.length ?? 0) > 0 || bySource) && (
          <FilterBar layout="segmented" filters={[sourceSpec]} values={controls.values} onChange={controls.onChange} />
        )}
        {chips.length > 1 && (
          <FilterBar layout="segmented" filters={[langSpec]} values={controls.values} onChange={controls.onChange} />
        )}
        {versions.length > 1 && (
          <FilterBar layout="segmented" filters={[versionSpec]} values={controls.values} onChange={controls.onChange} />
        )}
      </div>
      {bySource && (
        <p className="text-sm text-muted-foreground">
          Showing visitors, sign-ups and orders from source: {sourceLabel(source)}. These update once a day; taps are not
          split by source.
        </p>
      )}
      {figures.isError && <OrdersUnavailable error={figures.error} areaName={areaName} />}
      <DataTable
        columns={pageColumns(bySource, figures.isPending)}
        groups={groups}
        page={{ pageIndex: 0, pageSize: Math.max(shown.length, 1), rowCount: shown.length }}
        onPageChange={() => {}}
        onRowClick={(row) => controls.onChange({ ...controls.values, [PAGE]: row.folder })}
        emptyMessage="No pages in this funnel yet. Start with the language most of your traffic speaks."
        caption={`Pages in ${funnelName(funnelSlug)}`}
      />
      {!figures.isError && <OrdersStamp dataAsOf={figures.data?.meta.data_as_of} caption={PAGE_ORDERS_CAPTION} />}
      {unmatched && <p className="text-sm text-muted-foreground">{unmatched}</p>}
    </div>
  );
}
